import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils.js';

/**
 * Loading placeholders shaped like the content they stand in for, so the page does not jump
 * when the data lands. Every skeleton carries one accessible "loading" label for the whole
 * block — a screen reader announcing sixteen grey rectangles helps nobody.
 */

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse rounded-md bg-muted', className)}
      {...props}
    />
  );
}

export interface SkeletonBlockProps {
  className?: string;
  /** Overrides the default "Loading…" announcement. Pass an already-translated string. */
  label?: string;
  children: React.ReactNode;
}

/** Wraps a group of skeletons in a single polite live region. */
export function SkeletonBlock({ className, label, children }: SkeletonBlockProps) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-busy="true" aria-live="polite" className={className}>
      <span className="sr-only">{label ?? t('common.loading')}</span>
      {children}
    </div>
  );
}

/** A stack of list rows — the order list, the customer list, the payments list. */
export function ListSkeleton({ rows = 5, label }: { rows?: number; label?: string }) {
  return (
    <SkeletonBlock className="flex flex-col gap-3" {...(label ? { label } : {})}>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-center gap-3 rounded-lg border border-border p-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </SkeletonBlock>
  );
}

/** A grid of summary cards — the dashboard's top row. */
export function CardGridSkeleton({ cards = 4, label }: { cards?: number; label?: string }) {
  return (
    <SkeletonBlock
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
      {...(label ? { label } : {})}
    >
      {Array.from({ length: cards }, (_, index) => (
        <div key={index} className="flex flex-col gap-3 rounded-lg border border-border p-4">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-7 w-32" />
        </div>
      ))}
    </SkeletonBlock>
  );
}

/** A whole page: heading, then a list. */
export function PageSkeleton({ label }: { label?: string }) {
  return (
    <SkeletonBlock className="flex flex-col gap-6" {...(label ? { label } : {})}>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72 max-w-full" />
      </div>
      <div className="flex flex-col gap-3">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-16 w-full" />
        ))}
      </div>
    </SkeletonBlock>
  );
}
